import React from "react";
import PropTypes from "prop-types";
import {
  Divider,
  Typography,
  Grid,
  TextField,
  Button,
  Chip
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SimilarityPercentage from "components/CaseManagement/SimilarCases/SimilarityPercentage";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { DateWithTooltip } from "components/DateWithTooltip";
import { useHistory } from "react-router-dom";
import { getNumberOfDays } from "core/datetime";

const similarityFields = [
  {
    name: "Site",
    accessor: "site_name",
    similarityAccessor: "site_point"
  },
  {
    name: "Machine",
    accessor: "machine_name",
    similarityAccessor: "machine_point"
  },
  {
    name: "Make",
    accessor: "manufacturer_name",
    similarityAccessor: "manufacturer_point"
  },
  {
    name: "Model",
    accessor: "model_name",
    similarityAccessor: "model_point"
  },
  {
    name: "Component",
    accessor: "component_name",
    similarityAccessor: "component_point"
  },
  {
    name: "Failure Mode",
    accessor: "failure_mode_name",
    similarityAccessor: "failure_mode_point"
  }
];

export function SimilarCaseDetail({ similarCase }) {
  const history = useHistory();

  const openCaseInNewTab = () => {
    if (similarCase) {
      const handle = window.open(
        history.createHref({
          pathname: `${similarCase.id}`
        }),
        "_blank"
      );
      handle.blur();
      window.focus();
    }
  };

  return (
    <>
      <DetailDivider light />
      <div>
        <CaseIdText noWrap display="inline">
          #{similarCase.display_id}
        </CaseIdText>
        <Typography noWrap color="textSecondary" display="inline">
          |&nbsp;{similarCase.open ? "Open" : "Closed"}
        </Typography>
      </div>
      <SubtitleSection container justifyContent="space-between">
        <Typography noWrap color="textSecondary" variant="body2">
          <DateWithTooltip date={similarCase.open_date} showTime />
          <DaysOpenChip>
            {getNumberOfDays(
              similarCase.open_date,
              similarCase.close_date ?? new Date()
            )}{" "}
            days
          </DaysOpenChip>
        </Typography>
        <div>
          <SimilarityPercentage rawScore={similarCase.score} />
        </div>
      </SubtitleSection>
      <TextField
        disabled
        fullWidth
        multiline
        id="similar-case-description"
        label="Description"
        defaultValue={similarCase.description}
        variant="outlined"
      />
      <SimilaritySection>
        <SimilarityGroup
          type="Similar"
          similarCase={similarCase}
          similarityFields={similarityFields.filter(
            x => similarCase[x.similarityAccessor] > 0
          )}
        />
        <SimilaritySectionDivider light />
        <SimilarityGroup
          type="Not a match"
          similarCase={similarCase}
          similarityFields={similarityFields.filter(
            x => similarCase[x.similarityAccessor] === 0
          )}
        />
      </SimilaritySection>
      <Grid container justifyContent="flex-end">
        <Button
          variant="outlined"
          color="primary"
          data-qa="CP-Button-Open-Similar-Case-New-Tab"
          aria-label="Open case in new tab"
          onClick={() => openCaseInNewTab()}
        >
          Open Case in New Tab
        </Button>
      </Grid>
    </>
  );
}

function SimilarityGroup({ type, similarityFields, similarCase }) {
  return (
    <>
      <SimilaritySectionSubtitle container justifyContent="flex-start">
        <Typography variant="body2" color="textSecondary">
          {type}
        </Typography>
        <SimilarityChip
          color={type === "Similar" ? "primary" : "default"}
          size="small"
          label={similarityFields.length}
        />
      </SimilaritySectionSubtitle>
      {similarityFields.map(x => (
        <Typography key={x.name}>
          <BulletIcon color="primary" /> {x.name}:{" "}
          <FieldValue>{similarCase[x.accessor] ?? "None"}</FieldValue>
        </Typography>
      ))}
    </>
  );
}

SimilarityGroup.propTypes = {
  similarCase: PropTypes.object.isRequired,
  type: PropTypes.oneOf(["Similar", "Not a match"]),
  similarityFields: PropTypes.array.isRequired
};

const DaysOpenChip = styled("span")`
  margin-left: ${props => props.theme.spacing(1.5)};
  background-color: #c4c4c4;
  color: white;
  border-radius: 3px;
  padding: ${props => props.theme.spacing(0.5)}px
    ${props => props.theme.spacing(1)};
  font-weight: bold;
`;

const SimilarityChip = styled(Chip)`
  margin-left: ${props => props.theme.spacing(1)};
  font-size: 0.75rem;
`;

const SimilaritySectionSubtitle = styled(Grid)`
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const SimilaritySectionDivider = styled(Divider)`
  margin-top: ${props => props.theme.spacing(1)};
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const CaseIdText = styled(Typography)`
  padding-right: ${props => props.theme.spacing(1)};
`;

const DetailDivider = styled(Divider)`
  margin-bottom: ${props => props.theme.spacing(2)};
`;

const SubtitleSection = styled(Grid)`
  padding-top: ${props => props.theme.spacing(1)};
  padding-bottom: ${props => props.theme.spacing(2)};
`;

const SimilaritySection = styled("div")`
  padding-top: ${props => props.theme.spacing(2)};
  padding-bottom: ${props => props.theme.spacing(2)};
`;

const BulletIcon = styled(FiberManualRecordIcon)`
  font-size: 0.75rem;
`;

const FieldValue = styled("span")`
  font-weight: bold;
`;

SimilarCaseDetail.propTypes = {
  similarCase: PropTypes.object.isRequired
};
